import { useMemo } from 'react';
import { useUserMenuContext, UserMenuCategory as CtxCategory } from '../../../../context/UserMenuContext';
import { Food } from '../../userMenu.types';
import AddIcon from '@mui/icons-material/Add';
import searchIconSvg from '../../../../assets/svg/search-icon.svg';
import pageEmptySvg from '../../../../assets/svg/page-empty.svg';
import { useTranslation } from 'react-i18next';
import { LANG_TEXT } from '../../../../constants/language';

const formatPrice = (price: string | number) => {
    const num = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(num)) return '0';
    return num.toLocaleString('ja-JP');
};

function FoodRow({ food }: { food: Food }) {
    const imageUrl = Array.isArray(food.image_url) ? food.image_url[0] : food.image_url;

    return (
        <div style={{ padding: '16px 0', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8, position: 'relative' }}>
                <div style={{ display: 'flex', flexGrow: 1, minWidth: 0 }}>
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt={food.name || ''}
                            style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
                        />
                    ) : (
                        <div style={{
                            width: 100, height: 100, borderRadius: 8, flexShrink: 0, background: '#f3f4f6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <path d="M21 15l-5-5L5 21" />
                            </svg>
                        </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', marginLeft: 14, maxWidth: '72%' }}>
                        <p style={{
                            margin: 0, fontWeight: 500, fontSize: 15, color: '#111827',
                            maxHeight: '3em', overflow: 'hidden', textOverflow: 'ellipsis',
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        }}>
                            {food.name || ''}
                        </p>

                        {food.restaurantName && (
                            <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
                                {food.sold && food.sold > 0 ? `${food.sold} sold | ` : ''}{food.restaurantName}
                            </p>
                        )}
                        <p style={{ margin: 0, fontSize: 15, color: '#3D9F4A', fontWeight: 500 }}>
                            ¥ {formatPrice(food.price)}
                        </p>
                    </div>
                </div>

                {/* Right: + button */}
                <button
                    style={{
                        width: 32, height: 32, background: '#30893C', border: '1px solid #30893C',
                        borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, cursor: 'default',
                    }}
                >
                    <AddIcon style={{ color: '#fff', fontSize: 20 }} />
                </button>
            </div>
        </div>
    );
}

/* ── Skeleton rows ── */
function SkeletonRow() {
    return (
        <div style={{ padding: '14px 0', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 12 }}>
            <div style={{ width: 80, height: 80, borderRadius: 8, background: '#e5e7eb', flexShrink: 0 }} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, justifyContent: 'center' }}>
                <div style={{ height: 11, background: '#e5e7eb', borderRadius: 4 }} />
                <div style={{ height: 11, background: '#e5e7eb', borderRadius: 4, width: '55%' }} />
                <div style={{ height: 11, background: '#e5e7eb', borderRadius: 4, width: '35%' }} />
            </div>
        </div>
    );
}

/* ── Banner suggestion strip ── */
function BannerStrip({ foods }: { foods: Food[] }) {
    const { t } = useTranslation();
    if (foods.length === 0) return null;
    return (
        <div style={{ margin: '16px 0 16px 12px' }}>
            <p style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: '#111827' }}>{t(LANG_TEXT.HOME_SUGGESTED)}</p>
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 4 }}>
                {foods.map((food) => {
                    const img = Array.isArray(food.image_url) ? food.image_url[0] : food.image_url;
                    return (
                        <div key={food.id} style={{ flexShrink: 0, width: 110 }}>
                            <div style={{ width: 110, height: 110, borderRadius: 8, overflow: 'hidden', background: '#f3f4f6' }}>
                                {img && <img src={img} alt={food.name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                            </div>
                            <p style={{
                                margin: '4px 0 0', fontSize: 13, fontWeight: 500, color: '#111827',
                                overflow: 'hidden', textOverflow: 'ellipsis',
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                            }}>
                                {food.name || food.food_name}
                            </p>
                            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#22c55e', fontWeight: 600 }}>
                                ¥ {formatPrice(food.price)}
                            </p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ── Main ── */
export default function PhonePreview() {
    const { categories, activeCategoryId, setActiveCategoryId, loading } = useUserMenuContext();
    const { t } = useTranslation();

    const activeCategory: CtxCategory | undefined = useMemo(
        () => categories.find((c) => c.id === activeCategoryId) ?? categories[0],
        [categories, activeCategoryId]
    );

    const bannerFoods: Food[] = useMemo(
        () => (activeCategory?.foods ?? [])
            .filter((f) => f.banner && f.enabled !== false)
            .sort((a, b) => (a.banner_order ?? 9999) - (b.banner_order ?? 9999)),
        [activeCategory]
    );

    const listFoods: Food[] = useMemo(
        () => (activeCategory?.foods ?? []).filter((f) => f.enabled !== false),
        [activeCategory]
    );

    return (
        <div style={{
            width: '100%', height: '100%',
            background: '#fff',
            display: 'flex', flexDirection: 'column',
            fontFamily: '"Be Vietnam Pro", sans-serif',
            overflow: 'hidden',
        }}>
            <div style={{
                background: '#3D9F4A',
                paddingTop: 20,
                paddingBottom: 10,
                paddingLeft: 14,
                paddingRight: 14,
                flexShrink: 0,
                position: 'sticky', top: 0, zIndex: 10,
            }}>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: '#fff', borderRadius: 8, padding: '8px 10px',
                    border: '1px solid #d1d5db',
                }}>
                    <img src={searchIconSvg} alt="search" style={{ width: 16, height: 16 }} />
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>{t(LANG_TEXT.HOME_SEARCH_PLACEHOLDER)}</span>
                </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', scrollbarWidth: 'none' }}>
                {!loading && <BannerStrip foods={bannerFoods} />}
                <div style={{ padding: '12px 12px', display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none' }}>
                    {loading
                        ? [1, 2, 3].map((i) => (
                            <div key={i} style={{ flexShrink: 0, height: 32, width: 64, borderRadius: 6, background: '#e5e7eb' }} />
                        ))
                        : categories.map((cat) => {
                            const active = cat.id === (activeCategoryId ?? categories[0]?.id);
                            return (
                                <button
                                    key={cat.id}
                                    onClick={() => setActiveCategoryId(cat.id)}
                                    style={{
                                        flexShrink: 0,
                                        padding: '7px 14px',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        lineHeight: '22px',
                                        whiteSpace: 'nowrap',
                                        borderRadius: 8,
                                        border: 'none',
                                        cursor: 'pointer',
                                        background: active ? '#3D9F4A' : '#f3f4f6',
                                        color: active ? '#fff' : '#4b5563',
                                    }}
                                >
                                    {cat.title}
                                </button>
                            );
                        })}
                </div>

                {/* Food content */}
                <div style={{ padding: '12px 14px 24px' }}>
                    {loading ? (
                        <>
                            {[1, 2, 3, 4].map((i) => <SkeletonRow key={i} />)}
                        </>
                    ) : (
                        <>
                            {/* Food list */}
                            {listFoods.length === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', gap: 8, color: '#9ca3af' }}>
                                    <img src={pageEmptySvg} alt="empty" style={{ width: 80, height: 80 }} />
                                    <p style={{ fontSize: 11, margin: 0 }}>{t(LANG_TEXT.NO_FOODS_FOUND)}</p>
                                </div>
                            ) : (
                                listFoods.map((food) => <FoodRow key={food.id} food={food} />)
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
